/**
 * Loading Type. Used to represent data that is still loading, or has already been loaded.
 * @template T
 */
export type Loading<T> =
  | { status: 'not_started' }
  | { status: 'loading' }
  | { status: 'loaded'; data: T }
  | { status: 'error'; error: Error }

export const notStarted = { status: 'not_started' } as const
export const loading = { status: 'loading' } as const
export const loaded = <T>(data: T) => ({ status: 'loaded', data }) as Loading<T>
export const error = <T>(error: Error) => ({ status: 'error', error }) as Loading<T>

/**
 * Checks if the value is still loading.
 * @template T
 * @param {Loading<T>} value - The value to check.
 * @returns {value is { status: 'loading' }} - True if the value is still loading, false otherwise.
 */
export function isLoading<T>(value: Loading<T>): value is { status: 'loading' } {
  return value.status === 'loading'
}

/**
 * Checks if the value has already been loaded.
 * @template T
 * @param {Loading<T>} value - The value to check.
 * @returns {value is { status: 'loaded'; data: T }} - True if the value has already been loaded, false otherwise.
 */
export function isLoaded<T>(value: Loading<T>): value is { status: 'loaded'; data: T } {
  return value.status === 'loaded'
}

/**
 * Checks if the value has errored.
 * @template T
 * @param {Loading<T>} value - The value to check.
 * @returns {value is { status: 'error'; error: Error }} - True if the value has errored, false otherwise.
 */
export function isError<T>(value: Loading<T>): value is { status: 'error'; error: Error } {
  return value.status === 'error'
}

/**
 * Checks if the value has not started.
 * @template T
 * @param {Loading<T>} value - The value to check.
 * @returns {value is { status: 'not_started' }} - True if the value has not started, false otherwise.
 */
export function isNotStarted<T>(value: Loading<T>): value is { status: 'not_started' } {
  return value.status === 'not_started'
}
